import { Box, H4 } from '@deity/falcon-ui';
import type { IPropertyBag } from '@deity-io/di-data-basics';
import { mapRecordToArray } from '@deity-io/di-data-basics';
import Skeleton from '@material-ui/lab/Skeleton';
import { BaseHelmet, PackagesHeader, TRow, TableView, Thead } from 'components';
import { CACHE_FIRST } from 'contracts/constants';
import type { FC } from 'react';
import { useEffect } from 'react';
import { useGetFlowLazyQuery, useGetPackageLazyQuery } from 'services/graphql/generated/hooks';
import { validate as validateUuid } from 'uuid';
import type { IRuntimeState } from '../IRuntimeState';
import { isIExternalInstance } from './PackagePopoutFlow';
import { PackageVersionBadge } from './PackageVersionBadge';

interface IRestGatewayRowProps {
  isEditable?: boolean;
  itemKey: string;
  item: {
    accountUuid: string;
    path: string;
    method: string;
    packageVersionUuid: string;
    packageUuid: string;
    pipelineKey: string;
    count: number;
    instanceCount: number;
  };
}

const RestGatewayRow: FC<IRestGatewayRowProps> = ({ item, itemKey, isEditable = true }) => {
  const [getPackage, { loading: packageLoading, data: packageData }] = useGetPackageLazyQuery(CACHE_FIRST);

  const [getFlow, { loading: flowLoading, data: flowData }] = useGetFlowLazyQuery(CACHE_FIRST);
  useEffect(() => {
    if (item.packageUuid && validateUuid(item.packageUuid)) {
      getPackage({
        variables: {
          uuid: item.packageUuid
        }
      });
    }
    if (item.packageVersionUuid && validateUuid(item.packageVersionUuid)) {
      getFlow({
        variables: {
          packageVersionUuid: item.packageVersionUuid,
          uuid: item.pipelineKey.replaceAll('_', '-').substring(2)
        }
      });
    }
  }, [item]);

  return (
    <>
      <H4 fontSize="lg" color="primaryText">
        ...-{item.accountUuid.split('-').pop()}
      </H4>

      <H4 fontSize="lg" color="primaryText">
        {packageData?.getPackage.name || item.packageUuid}
        <PackageVersionBadge packageVersionKey={item.packageVersionUuid} />
      </H4>

      <H4 fontSize="lg" color="primaryText" css={{ width: '100%' }}>
        {flowData?.getFlow.name || item.pipelineKey}
      </H4>

      <H4 fontSize="lg" color="primaryText" fontWeight="bold">
        {item.method}
      </H4>

      <H4 fontSize="lg" color="primaryText" css={{ width: '100%' }}>
        {item.path}
      </H4>
      <H4 fontSize="lg" color="primaryText" mr="xs">
        {item.instanceCount} / {item.instanceCount}
      </H4>
      <H4 fontSize="lg" color="primaryText">
        {item.count}
      </H4>
    </>
  );
};

interface IRestGatewayTab {
  isEditable?: boolean;
  loading?: boolean;
  loaded?: boolean;
  clusterState: IRuntimeState;
}
const instanceTypeFilter = 'di-rest-gateway';
const RestGatewayTab: FC<IRestGatewayTab> = ({ isEditable = true, clusterState, loading, loaded }) => {
  if (!(instanceTypeFilter in clusterState.services)) {
    return null;
  }
  const gatewayInstances = clusterState.services[instanceTypeFilter];

  const paths: IPropertyBag = {};
  mapRecordToArray(clusterState.packages, (pack, packageKey) => {
    if (!pack.instances.find(instanceId => instanceId in gatewayInstances)) {
      return undefined;
    }

    mapRecordToArray(pack.versions, (version, versionKey) => {
      mapRecordToArray(version.pipelines, (pipeline, pipelineKey) => {
        mapRecordToArray(pipeline.instances, (instance, instanceKey) => {
          if (!pack.instances.find(instanceId => instanceId in gatewayInstances)) {
            return undefined;
          }
          if (isIExternalInstance(instance.data)) {
            mapRecordToArray(instance.data.components, component => {
              const key = `${pack.accountUuid}${component.path}`;
              if (!(key in paths)) {
                paths[key] = {
                  ...component,
                  accountUuid: pack.accountUuid,
                  packageVersionUuid: versionKey,
                  packageUuid: pack.uuid,
                  pipelineKey,
                  instanceCount: 0,
                  count: 0
                };
              }
              paths[key].instanceCount += 1;
            });
          }
        });
      });
    });
  });

  return (
    <>
      <BaseHelmet page="Packages" />
      <Box>
        <PackagesHeader />
        <Box
          style={{ border: '1px solid #E0E2E5' }}
          borderRadius="medium"
          pt="lg"
          pb="lg"
          bg="white"
          boxShadow="subtle"
        >
          <TableView gridTemplateColumns="1fr 1fr 1fr 0.2fr 1fr 0.4fr 0.4fr 0.4fr">
            <Thead
              headerItems={[
                { text: 'account' },
                { text: 'package' },
                { text: 'flow' },
                { text: 'method' },
                { text: 'path' },
                { text: 'instances' },
                { text: 'Msg count' },
                { text: 'actions' }
              ]}
            />

            {mapRecordToArray(paths, (item, itemKey) => (
              <TRow key={itemKey} rowMaxHeight="550px">
                <RestGatewayRow item={item} itemKey={itemKey} isEditable={isEditable} />
              </TRow>
            ))}
            {loading && (
              <TRow>
                <>
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                </>
              </TRow>
            )}
          </TableView>
        </Box>
      </Box>
    </>
  );
};

export default RestGatewayTab;
